// VideoFrame.tsx — video phase 2 (2026-09-26): an uploaded clip in a post.
//
// Same height budget and width-from-height rule as MediaFrame, so a portrait clip narrows
// instead of towering over the feed. The wrapper is a div, NOT a button: a button would
// swallow the native controls (PostCard's card-click guard skips `video` for the same reason).
//
// Plays the 720p light copy by default — kinder to data plans — with an "HD" switch to the
// full-quality file when there is one. preload="none": nothing is fetched until the member
// presses play; the poster frame is all the feed downloads.

import { useRef, useState, type CSSProperties } from 'react';
import styled from 'styled-components';
import type { PostVideo } from '@/core/models/post';
import { Icon } from '@/icons';
import { mediaCap, mediaCapHero } from './mediaCap';
import { resolveFit } from './frameKit';
import { formatDuration } from './formatDuration';
import { useViewReporter } from './useViewReporter';

const Frame = styled.div<{ $hero: boolean }>`
  ${({ $hero }) => ($hero ? mediaCapHero : mediaCap)}
  position: relative;
  display: block;
  width: min(100%, calc(var(--media-cap) * var(--ar)));
  aspect-ratio: var(--ar);
  max-height: var(--media-cap);
  /* Same reason as MediaFrame: the card is a column flexbox. */
  align-self: center;
  line-height: 0;
`;

const Clip = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: #000;

  & > video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
  }
`;

const HdToggle = styled.button<{ $on: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: ${({ $on }) => ($on ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.55)')};
  color: ${({ $on }) => ($on ? '#000' : '#fff')};
  font: 600 11px/1.6 ${({ theme }) => theme.font.body};
  letter-spacing: 0.04em;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// Media-grid tile: a still with a play badge and the length. Plain div — grid tiles are
// already wrapped in a link to the post.
const FillFrame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: #000;
  & > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Badge = styled.span`
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font: 600 11px/1.4 ${({ theme }) => theme.font.body};
`;

export interface VideoFrameProps {
  video: PostVideo;
  fill?: boolean;
  variant?: 'feed' | 'hero';
}

export function VideoFrame({ video, fill = false, variant = 'feed' }: VideoFrameProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const hasHd = video.url !== video.lightUrl;
  const [hd, setHd] = useState(false);
  const duration = formatDuration(video.durationMs);
  // Grid tiles have no player, so the ref stays null there and nothing is reported.
  useViewReporter(ref, video.id);

  if (fill) {
    return (
      <FillFrame data-testid="post-media-video">
        {video.posterUrl ? <img src={video.posterUrl} alt="" loading="lazy" /> : null}
        <Badge>
          <Icon name="play" size={12} />
          {duration}
        </Badge>
      </FillFrame>
    );
  }

  function toggleHd() {
    const el = ref.current;
    const at = el?.currentTime ?? 0;
    const wasPlaying = el ? !el.paused && !el.ended : false;
    setHd((on) => !on);
    // Keep the member's place: once the new source has its metadata, seek back and resume.
    if (el) {
      const resume = () => {
        el.currentTime = at;
        if (wasPlaying) void el.play().catch(() => {});
      };
      el.addEventListener('loadedmetadata', resume, { once: true });
    }
  }

  const { ar } = resolveFit(video.width / video.height);
  return (
    <Frame
      $hero={variant === 'hero'}
      style={{ '--ar': ar } as CSSProperties}
      data-testid="post-media-video"
    >
      <Clip>
        <video
          ref={ref}
          src={hd ? video.url : video.lightUrl}
          poster={video.posterUrl ?? undefined}
          controls
          playsInline
          preload="none"
          aria-label={`Video, ${duration}`}
        />
      </Clip>
      {hasHd && (
        <HdToggle
          type="button"
          $on={hd}
          aria-pressed={hd}
          aria-label={hd ? 'Switch to standard quality' : 'Switch to HD'}
          onClick={toggleHd}
        >
          HD
        </HdToggle>
      )}
    </Frame>
  );
}
